import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { MapPin, ArrowRight, ChevronDown } from "lucide-react";
import NewsCard from "./NewsCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LS_KEY = "newseze.metro";

/**
 * NearYouStrip — IP-geo-detected local news for the current reader, with a
 * "Change metro" dropdown so users can override the auto-detect (and we
 * remember the choice in localStorage). Falls back gracefully when the
 * backend can't determine a metro.
 *
 * Story cards use the same NewsCard component as every other home section,
 * so Why It Matters / Brief Me / Full Analysis / Share are all available.
 */
export default function NearYouStrip({ onBrief, onShare }) {
  const [data, setData] = useState(null);
  const [metros, setMetros] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [savedMetro, setSavedMetro] = useState(() => {
    try { return localStorage.getItem(LS_KEY) || ""; } catch { return ""; }
  });

  useEffect(() => {
    axios.get(`${API}/geo/metros`, { timeout: 6000 })
      .then((r) => setMetros(r.data?.metros || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    let alive = true;
    const url = savedMetro
      ? `${API}/geo/local?limit=6&metro=${savedMetro}`
      : `${API}/geo/local?limit=6`;
    axios.get(url, { timeout: 12000 })
      .then(async (r) => {
        if (!alive) return;
        // Backend couldn't detect a metro (CDN/k8s strips real client IP).
        // Try a browser-side geo lookup — same ip-api endpoint, but called
        // from the user's actual IP this time. If it succeeds, re-fetch.
        if (!savedMetro && (!r.data?.metro_id || r.data?.source === "fallback")) {
          try {
            const g = await axios.get("https://ip-api.com/json/?fields=status,countryCode,region,regionName,city", { timeout: 4000 });
            if (alive && g.data?.status === "success" && g.data?.countryCode === "US") {
              const city = (g.data.city || "").toLowerCase();
              // Pull metros from backend, find best city match
              const m = await axios.get(`${API}/geo/metros`, { timeout: 4000 });
              const metros = m.data?.metros || [];
              // crude city→metro match; backend keeps the canonical list
              const cityHits = {
                "san diego": "san-diego", "los angeles": "los-angeles", "new york": "new-york",
                "san francisco": "bay-area", "oakland": "bay-area", "san jose": "bay-area",
                "dallas": "dallas", "houston": "houston", "austin": "austin",
                "miami": "miami", "tampa": "tampa", "orlando": "orlando",
                "atlanta": "atlanta", "seattle": "seattle", "phoenix": "phoenix",
                "denver": "denver", "philadelphia": "philadelphia", "cleveland": "cleveland",
                "chicago": "chicago", "boston": "boston", "detroit": "detroit",
              };
              const matched = Object.entries(cityHits).find(([k]) => city.includes(k));
              if (matched && metros.find((mm) => mm.id === matched[1])) {
                const r2 = await axios.get(`${API}/geo/local?limit=6&metro=${matched[1]}`, { timeout: 8000 });
                if (alive) setData({ ...r2.data, _browserGeo: true });
                return;
              }
            }
          } catch {}
        }
        setData(r.data);
      })
      .catch(() => { if (alive) setHidden(true); });
    return () => { alive = false; };
  }, [savedMetro]);

  const pickMetro = (mid) => {
    setSavedMetro(mid);
    try {
      if (mid) localStorage.setItem(LS_KEY, mid);
      else localStorage.removeItem(LS_KEY);
    } catch {}
    setPickerOpen(false);
  };

  if (hidden || !data) return null;
  // Honest empty state — if a metro has no fresh photo'd local stories, show
  // a calm "check back soon" note instead of swapping in unrelated content.
  if (!data.items || data.items.length === 0) {
    if (data.source === "empty_metro") {
      return (
        <section className="my-6 rounded-2xl border border-[var(--line)] bg-white px-5 py-4" data-testid="near-you-strip">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <MapPin size={16} className="text-[var(--gold)]" />
              <div className="text-[11px] uppercase tracking-[0.2em] font-black text-[var(--blue)]">Near You</div>
              <span className="text-[15px] font-black text-[var(--ink)] tracking-tight">· {data.metro_label}</span>
              <button onClick={() => setPickerOpen(!pickerOpen)} className="text-[10.5px] font-bold text-[var(--blue)] inline-flex items-center gap-0.5 ml-1 hover:underline" data-testid="near-you-change-metro">
                Change <ChevronDown size={11} />
              </button>
            </div>
          </div>
          <div className="mt-2 text-[13px] text-[var(--muted)]" data-testid="near-you-empty">
            {data.note || `No fresh local stories for ${data.metro_label} right now. Check back soon.`}
          </div>
          {pickerOpen && (
            <div className="absolute mt-1 z-30 bg-white border border-[var(--line)] rounded-lg shadow-lg p-2 max-h-[300px] overflow-y-auto w-[220px]">
              <button onClick={() => pickMetro("")} className="w-full text-left text-[12px] font-bold text-[var(--muted)] hover:bg-[#fff9ed] px-2 py-1.5 rounded">Auto-detect from IP</button>
              <div className="h-px bg-[var(--line)] my-1" />
              {metros.map((m) => (
                <button key={m.id} onClick={() => pickMetro(m.id)} className={`w-full text-left text-[12.5px] hover:bg-[#fff9ed] px-2 py-1.5 rounded ${savedMetro === m.id ? "font-black text-[var(--blue)] bg-[#fff7d6]" : "text-[var(--ink)]"}`}>
                  {m.label}
                </button>
              ))}
            </div>
          )}
        </section>
      );
    }
    return null;
  }

  const label = data.metro_label || "Near You";

  return (
    <section className="my-6 rounded-2xl border border-[var(--line)] bg-white overflow-hidden" data-testid="near-you-strip">
      <div className="flex items-center justify-between gap-2 px-5 py-3 bg-gradient-to-r from-[#fff9ed] to-white border-b border-[var(--line)] relative">
        <div className="flex items-center gap-2">
          <MapPin size={16} className="text-[var(--gold)]" />
          <div className="text-[11px] uppercase tracking-[0.2em] font-black text-[var(--blue)]">
            Near You
          </div>
          <span className="text-[15px] font-black text-[var(--ink)] tracking-tight" data-testid="near-you-metro-label">
            · {label}
          </span>
          <button
            onClick={() => setPickerOpen(!pickerOpen)}
            className="text-[10.5px] font-bold text-[var(--blue)] inline-flex items-center gap-0.5 ml-1 hover:underline"
            data-testid="near-you-change-metro"
          >
            Change <ChevronDown size={11} />
          </button>
          {pickerOpen && (
            <div className="absolute top-full left-5 mt-1 z-30 bg-white border border-[var(--line)] rounded-lg shadow-lg p-2 max-h-[300px] overflow-y-auto w-[220px]" data-testid="near-you-metro-picker">
              <button
                onClick={() => pickMetro("")}
                className="w-full text-left text-[12px] font-bold text-[var(--muted)] hover:bg-[#fff9ed] px-2 py-1.5 rounded"
                data-testid="near-you-metro-auto"
              >
                Auto-detect from IP
              </button>
              <div className="h-px bg-[var(--line)] my-1" />
              {metros.map((m) => (
                <button
                  key={m.id}
                  onClick={() => pickMetro(m.id)}
                  className={`w-full text-left text-[12.5px] hover:bg-[#fff9ed] px-2 py-1.5 rounded ${savedMetro === m.id ? "font-black text-[var(--blue)] bg-[#fff7d6]" : "text-[var(--ink)]"}`}
                  data-testid={`near-you-pick-${m.id}`}
                >
                  {m.label}
                </button>
              ))}
            </div>
          )}
        </div>
        <Link to="/category/local" className="text-[12px] font-black text-[var(--blue)] inline-flex items-center gap-1" data-testid="near-you-more">
          More local <ArrowRight size={12} />
        </Link>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
        {data.items.slice(0, 6).map((a) => (
          <NewsCard key={a.id} article={a} onBrief={onBrief} onShare={onShare} />
        ))}
      </div>
      {data.source === "fallback" && !savedMetro && (
        <div className="px-5 py-2 text-[10.5px] text-[var(--muted)] bg-[#fafaf6]" data-testid="near-you-fallback-note">
          Showing national-local until we can detect your metro. Tap "Change" to pick your city.
        </div>
      )}
    </section>
  );
}
